// Reports protocol — the cloud copies of delivered PDFs (Owner decision list 2026-10-19 items 25 / 27 / 28),
// shared by the app (src/backend/reportsClient.ts) and the reports-v2 edge function. Pure TypeScript.
//
//   upload  POST …/reports-v2?action=upload   body = the PDF bytes (application/pdf), metadata in the
//                                              X-Report-Meta header (base64url JSON) — idempotent per
//                                              (account, report id)
//   list    POST { action: 'list' }           the account's delivered PDFs, newest first
//   link    POST { action: 'link', fileId }   a download link valid for LINK_SECONDS, made only after the
//                                              caller proved it owns the file ("先验身份再发临时链接")
export const REPORTS_PROTOCOL_VERSION = 1;
export const REPORTS_FUNCTION_PATH = '/functions/v1/reports-v2';
export const MAX_REPORT_BYTES = 15_000_000;
export const LINK_SECONDS = 300;

export type CloudReportVariant = 'clientEnglish' | 'selfChinese' | 'clientChineseSummary' | 'agreement' | 'addendum';

export const CLOUD_REPORT_VARIANTS: readonly CloudReportVariant[] = Object.freeze(['clientEnglish', 'selfChinese', 'clientChineseSummary', 'agreement', 'addendum']);

export interface ReportUploadMeta {
  reportId: string;
  inspectionId: string;
  variant: CloudReportVariant;
  fileName: string;
  pageCount: number;
  generatedAt: number;
  address: string;
}

export interface CloudReportFile {
  id: string;
  reportId: string;
  inspectionId: string;
  variant: CloudReportVariant;
  fileName: string;
  byteSize: number;
  pageCount: number;
  address: string;
  generatedAt: number;
}

export type ReportsErrorCode = 'unauthenticated' | 'badRequest' | 'tooLarge' | 'notFound' | 'storageUnavailable' | 'internalError';

export const REPORTS_ERROR_STATUS: Readonly<Record<ReportsErrorCode, number>> = Object.freeze({
  unauthenticated: 401,
  badRequest: 400,
  tooLarge: 413,
  notFound: 404,
  storageUnavailable: 503,
  internalError: 500
});

export type ReportsResponse =
  | { ok: true; protocolVersion: typeof REPORTS_PROTOCOL_VERSION; action: 'upload'; file: CloudReportFile }
  | { ok: true; protocolVersion: typeof REPORTS_PROTOCOL_VERSION; action: 'list'; files: CloudReportFile[] }
  | { ok: true; protocolVersion: typeof REPORTS_PROTOCOL_VERSION; action: 'link'; url: string; expiresAt: number }
  | { ok: false; protocolVersion: typeof REPORTS_PROTOCOL_VERSION; code: ReportsErrorCode; message: string };

export function reportsError(code: ReportsErrorCode, message: string): ReportsResponse {
  return { ok: false, protocolVersion: REPORTS_PROTOCOL_VERSION, code, message };
}

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function idOk(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_:.-]{1,120}$/.test(value);
}

export function isCloudReportVariant(value: unknown): value is CloudReportVariant {
  return typeof value === 'string' && (CLOUD_REPORT_VARIANTS as readonly string[]).includes(value);
}

function base64UrlEncode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (ch) => ch.charCodeAt(0)));
}

export function encodeUploadMeta(meta: ReportUploadMeta): string {
  return base64UrlEncode(JSON.stringify(meta));
}

export function decodeUploadMeta(header: string | null): ReportUploadMeta | null {
  if (!header || header.length > 4_000) return null;
  try {
    const raw = JSON.parse(base64UrlDecode(header)) as unknown;
    if (!isRec(raw) || !idOk(raw.reportId) || !idOk(raw.inspectionId) || !isCloudReportVariant(raw.variant)) return null;
    if (typeof raw.fileName !== 'string' || raw.fileName.trim().length === 0 || raw.fileName.length > 200) return null;
    const pageCount = typeof raw.pageCount === 'number' && Number.isFinite(raw.pageCount) ? Math.max(0, Math.min(2000, Math.floor(raw.pageCount))) : 0;
    const generatedAt = typeof raw.generatedAt === 'number' && Number.isFinite(raw.generatedAt) ? raw.generatedAt : Date.now();
    return { reportId: raw.reportId, inspectionId: raw.inspectionId, variant: raw.variant, fileName: raw.fileName.trim(), pageCount, generatedAt, address: typeof raw.address === 'string' ? raw.address.slice(0, 200) : '' };
  } catch {
    return null;
  }
}

export function checkCloudReportFile(raw: unknown): CloudReportFile | null {
  if (!isRec(raw) || typeof raw.id !== 'string' || !idOk(raw.reportId) || !idOk(raw.inspectionId) || !isCloudReportVariant(raw.variant) || typeof raw.fileName !== 'string') return null;
  const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
  return { id: raw.id, reportId: raw.reportId, inspectionId: raw.inspectionId, variant: raw.variant, fileName: raw.fileName, byteSize: num(raw.byteSize), pageCount: num(raw.pageCount), address: typeof raw.address === 'string' ? raw.address : '', generatedAt: num(raw.generatedAt) };
}

export function checkReportsResponse(raw: unknown): ReportsResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== REPORTS_PROTOCOL_VERSION) return null;
  if (raw.ok === false) {
    const code = typeof raw.code === 'string' && raw.code in REPORTS_ERROR_STATUS ? (raw.code as ReportsErrorCode) : null;
    return code ? { ok: false, protocolVersion: REPORTS_PROTOCOL_VERSION, code, message: typeof raw.message === 'string' ? raw.message : '' } : null;
  }
  if (raw.ok !== true) return null;
  if (raw.action === 'upload') {
    const file = checkCloudReportFile(raw.file);
    return file ? { ok: true, protocolVersion: REPORTS_PROTOCOL_VERSION, action: 'upload', file } : null;
  }
  if (raw.action === 'list' && Array.isArray(raw.files)) {
    const files = raw.files.map(checkCloudReportFile).filter((file): file is CloudReportFile => file !== null);
    return { ok: true, protocolVersion: REPORTS_PROTOCOL_VERSION, action: 'list', files };
  }
  if (raw.action === 'link' && typeof raw.url === 'string' && /^https:\/\//.test(raw.url)) {
    return { ok: true, protocolVersion: REPORTS_PROTOCOL_VERSION, action: 'link', url: raw.url, expiresAt: typeof raw.expiresAt === 'number' ? raw.expiresAt : 0 };
  }
  return null;
}

// Where a PDF lives in the bucket: the account, the inspection, the app's report id.
export function objectKeyOf(userId: string, meta: Pick<ReportUploadMeta, 'inspectionId' | 'reportId'>): string {
  return `${userId}/${meta.inspectionId}/${meta.reportId}.pdf`;
}
